// V4.2 — Today's AI Insight: deterministic daily market facts rendered
// via i18n templates (no LLM in this path). Every bullet is clickable
// and starts the matching chat question.
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, ChevronRight } from "lucide-react";

function fmtEur(v) {
  if (v == null) return "—";
  return `€ ${Math.round(v).toLocaleString("de-AT")}`;
}

export function InsightBoard({ insights, onAsk }) {
  const { t } = useTranslation("ai");
  const navigate = useNavigate();
  const f = insights?.facts;
  if (!f) return null;

  const bullets = [];
  if (f.active_total > 0) {
    bullets.push({
      key: "active",
      text: t("insights.activeTotal", { count: f.active_total }),
      ask: t("insights.askMarket"),
    });
  }
  if (f.new_listings_24h > 0) {
    bullets.push({
      key: "new24h",
      text: t("insights.new24h", { count: f.new_listings_24h }),
      ask: t("insights.askNew"),
    });
  }
  const autoUnder = f.by_vertical?.auto?.under_market || 0;
  if (autoUnder > 0) {
    bullets.push({
      key: "autoUnder",
      text: t("insights.autoUnder", { count: autoUnder }),
      ask: t("insights.askAutoDeals"),
    });
  }
  const reUnder = f.by_vertical?.real_estate?.under_market || 0;
  if (reUnder > 0) {
    bullets.push({
      key: "reUnder",
      text: t("insights.reUnder", { count: reUnder }),
      ask: t("insights.askReDeals"),
    });
  }
  if (f.top_yield_region?.region) {
    bullets.push({
      key: "yield",
      text: t("insights.topYield", {
        region: f.top_yield_region.region,
        pct: f.top_yield_region.yield_pct,
      }),
      ask: t("insights.askYield", { region: f.top_yield_region.region }),
    });
  }
  if (f.biggest_price_drop?.drop_pct) {
    bullets.push({
      key: "drop",
      text: t("insights.priceDrop", {
        pct: Math.abs(f.biggest_price_drop.drop_pct),
      }),
      listing: f.biggest_price_drop.ulid,
    });
  }
  if (f.top_deal?.ulid) {
    bullets.push({
      key: "topDeal",
      text: t("insights.topDeal", {
        title: (f.top_deal.title || "").slice(0, 48),
        pct: Math.abs(Math.round(f.top_deal.pct || 0)),
      }),
      listing: f.top_deal.ulid,
    });
  }
  if (!bullets.length) return null;

  return (
    <div
      data-testid="ai-insight-board"
      className="rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/[0.07] to-transparent p-5 text-left"
    >
      <div className="flex items-center gap-2 text-sm font-medium text-amber-300">
        <Sparkles className="h-4 w-4" />
        {t("insights.heading")}
      </div>
      <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
        {bullets.slice(0, 6).map((b) => (
          <button
            key={b.key}
            type="button"
            data-testid={`ai-insight-${b.key}`}
            onClick={() =>
              b.listing ? navigate(`/listing/${b.listing}`) : onAsk(b.ask)
            }
            className="group flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-sm text-zinc-300 transition hover:bg-zinc-900/80 hover:text-zinc-100"
          >
            <span>{b.text}</span>
            <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-zinc-600 transition group-hover:text-amber-400" />
          </button>
        ))}
      </div>
    </div>
  );
}
